import { useEffect } from 'react';
import { TreeInteractor } from './TreeInteractor';

export type UndoRedoAction = 'undo' | 'redo';

/** Subset of KeyboardEvent that {@link resolveUndoRedoAction} inspects (testable without real events). */
export interface UndoRedoKeyInput {
    key: string;
    metaKey: boolean;
    ctrlKey: boolean;
    altKey: boolean;
    shiftKey: boolean;
    repeat: boolean;
    target: EventTarget | null;
}

/**
 * True when the keydown originated in a control that needs native editing keys —
 * inputs, textareas, contenteditable regions, or the CodeMirror-based guide editor.
 * Those must keep native undo/redo (Cmd/Ctrl+Z, Cmd/Ctrl+Shift+Z).
 */
export function isTextEntryTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    const tag = target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (target instanceof HTMLElement && target.isContentEditable) return true;
    // The SimpleMDE guide editor is CodeMirror-based; focus normally lands on its
    // hidden textarea (covered above), but guard the wrapper too.
    return target.closest('.CodeMirror, .EasyMDEContainer') !== null;
}

/**
 * Maps a keydown to a tree-editor history action: Cmd/Ctrl+Z undoes, Cmd/Ctrl+Shift+Z
 * redoes. Returns null for anything else, including OS key auto-repeat (holding the
 * chord must not machine-gun through history — and each step also draft-saves) and
 * any key pressed while a text-entry control has focus.
 */
export function resolveUndoRedoAction(event: UndoRedoKeyInput): UndoRedoAction | null {
    if (!(event.metaKey || event.ctrlKey) || event.altKey) return null;
    if (event.repeat) return null;
    if (isTextEntryTarget(event.target)) return null;
    if (event.key.toLowerCase() !== 'z') return null;
    return event.shiftKey ? 'redo' : 'undo';
}

/** "Cmd" on Apple platforms, "Ctrl" elsewhere — for tooltips/aria-labels. */
export function undoRedoModifierLabel(): 'Cmd' | 'Ctrl' {
    const platform = typeof navigator !== 'undefined' ? navigator.platform || navigator.userAgent : '';
    return /Mac|iP(hone|ad|od)/.test(platform) ? 'Cmd' : 'Ctrl';
}

/**
 * Registers the editor's undo/redo keyboard shortcuts for the lifetime of the component.
 * Only prevents the browser default when it actually performs an undo/redo, so native
 * text editing shortcuts keep working everywhere else.
 */
export function useUndoRedoShortcuts(interactor: TreeInteractor): void {
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const action = resolveUndoRedoAction(event);
            if (action === 'undo' && interactor.canUndo) {
                event.preventDefault();
                interactor.undo();
            } else if (action === 'redo' && interactor.canRedo) {
                event.preventDefault();
                interactor.redo();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [interactor]);
}
